import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Shield, AlertCircle } from 'lucide-react';
import { Button } from '@travellegacy/nexops-design-system';
import { Card, CardContent } from '@travellegacy/nexops-design-system';
import { toast } from '@/shared/lib/toast';
import { usersAPI } from '@/shared/lib/api';
import { isAxiosError } from 'axios';
import type { User } from '@/shared/types';
import {
  Permission,
  PERMISSION_LABELS,
  PERMISSION_DESCRIPTIONS,
  type UserPermissions,
} from '@/shared/types/permissions';
import { useAuth } from '@/shared/contexts/AuthContext';

interface PermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
}

export function PermissionsModal({ isOpen, onClose, user }: PermissionsModalProps) {
  const queryClient = useQueryClient();
  const { user: currentUser, refreshCurrentUser } = useAuth();
  const [permissions, setPermissions] = useState<UserPermissions>({
    FINANCE: false,
    FINANCE_RECEIPT: false,
    AUTOMATIONS: false,
    PEOPLE_CORE: false,
    PEOPLE_CORE_MANAGEMENT: false,
    CLIENT_OPERATIONS: false,
    WHATSAPP_OPERATIONS: false,
    INCIDENT_MANAGEMENT: false,
    INSTAGRAM: false,
    BLOG: false,
    FEEDBACK_PDI: false,
    FEEDBACK_PDI_MANAGEMENT: false,
    TRAINING: false,
  });

  // Initialize permissions from user data
  useEffect(() => {
    if (user?.permissions) {
      setPermissions({
        FINANCE: user.permissions.FINANCE ?? false,
        FINANCE_RECEIPT: user.permissions.FINANCE_RECEIPT ?? false,
        AUTOMATIONS: user.permissions.AUTOMATIONS ?? false,
        PEOPLE_CORE: user.permissions.PEOPLE_CORE ?? false,
        PEOPLE_CORE_MANAGEMENT: user.permissions.PEOPLE_CORE_MANAGEMENT ?? false,
        CLIENT_OPERATIONS: user.permissions.CLIENT_OPERATIONS ?? false,
        WHATSAPP_OPERATIONS: user.permissions.WHATSAPP_OPERATIONS ?? false,
        INCIDENT_MANAGEMENT: user.permissions.INCIDENT_MANAGEMENT ?? false,
        INSTAGRAM: user.permissions.INSTAGRAM ?? false,
        BLOG: user.permissions.BLOG ?? false,
        FEEDBACK_PDI: user.permissions.FEEDBACK_PDI ?? false,
        FEEDBACK_PDI_MANAGEMENT: user.permissions.FEEDBACK_PDI_MANAGEMENT ?? false,
        TRAINING: user.permissions.TRAINING ?? false,
      });
    } else {
      setPermissions({
        FINANCE: false,
        FINANCE_RECEIPT: false,
        AUTOMATIONS: false,
        PEOPLE_CORE: false,
        PEOPLE_CORE_MANAGEMENT: false,
        CLIENT_OPERATIONS: false,
        WHATSAPP_OPERATIONS: false,
        INCIDENT_MANAGEMENT: false,
        INSTAGRAM: false,
        BLOG: false,
        FEEDBACK_PDI: false,
        FEEDBACK_PDI_MANAGEMENT: false,
        TRAINING: false,
      });
    }
  }, [user]);

  const updatePermissionsMutation = useMutation({
    mutationFn: (newPermissions: Partial<UserPermissions>) =>
      usersAPI.updatePermissions(user!.id, newPermissions),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });

      // Se estamos editando o próprio usuário logado, recarregar seus dados
      if (user?.id === currentUser?.id) {
        try {
          await refreshCurrentUser();
        } catch (error) {
          console.error('Failed to refresh current user after permission update:', error);
        }
      }

      toast.success('Sucesso', 'Permissões atualizadas com sucesso!');
      onClose();
    },
    onError: (error) => {
      const errorMessage = isAxiosError(error)
        ? error.response?.data?.error?.message || 'Erro ao atualizar permissões'
        : 'Erro desconhecido';
      toast.error('Erro', errorMessage);
    },
  });

  const handleToggle = (permission: Permission) => {
    setPermissions((prev) => ({
      ...prev,
      [permission]: !prev[permission],
    }));
  };

  const handleSelectAll = () => {
    setPermissions({
      FINANCE: true,
      FINANCE_RECEIPT: true,
      AUTOMATIONS: true,
      PEOPLE_CORE: true,
      PEOPLE_CORE_MANAGEMENT: true,
      CLIENT_OPERATIONS: true,
      WHATSAPP_OPERATIONS: true,
      INCIDENT_MANAGEMENT: true,
      INSTAGRAM: true,
      BLOG: true,
      FEEDBACK_PDI: true,
      FEEDBACK_PDI_MANAGEMENT: true,
      TRAINING: true,
    });
  };

  const handleDeselectAll = () => {
    setPermissions({
      FINANCE: false,
      FINANCE_RECEIPT: false,
      AUTOMATIONS: false,
      PEOPLE_CORE: false,
      PEOPLE_CORE_MANAGEMENT: false,
      CLIENT_OPERATIONS: false,
      WHATSAPP_OPERATIONS: false,
      INCIDENT_MANAGEMENT: false,
      INSTAGRAM: false,
      BLOG: false,
      FEEDBACK_PDI: false,
      FEEDBACK_PDI_MANAGEMENT: false,
      TRAINING: false,
    });
  };

  const handleSubmit = () => {
    updatePermissionsMutation.mutate(permissions);
  };

  const isAdmin = user?.role === 'ADMIN';
  const isClient = user?.role === 'CLIENT';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Gerenciar Permissões</h2>
              <p className="text-sm text-muted-foreground">
                {user?.name} ({user?.email})
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <CardContent className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Info sobre regras de negócio */}
          {isAdmin && (
            <div className="flex items-start space-x-3 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-500">Administrador</p>
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                  Administradores sempre têm acesso total a todos os módulos, independente
                  das permissões configuradas.
                </p>
              </div>
            </div>
          )}

          {isClient && (
            <div className="flex items-start space-x-3 p-4 rounded-lg bg-orange-500/10 border border-orange-500/20">
              <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-orange-500">Cliente</p>
                <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                  Clientes nunca têm acesso a módulos internos do sistema. Acesso restrito
                  apenas ao dashboard de fidelidade.
                </p>
              </div>
            </div>
          )}

          {!isAdmin && !isClient && (
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Ações Rápidas</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleSelectAll}>
                  Selecionar Todos
                </Button>
                <Button variant="outline" size="sm" onClick={handleDeselectAll}>
                  Desmarcar Todos
                </Button>
              </div>
            </div>
          )}

          {/* Permission Checkboxes */}
          <div className="space-y-4">
            {Object.values(Permission).map((permission) => (
              <label
                key={permission}
                className={`flex items-start space-x-4 p-4 rounded-lg border transition-colors ${
                  permissions[permission]
                    ? 'bg-primary/5 border-primary/30'
                    : 'hover:bg-accent border-border'
                } ${isAdmin || isClient ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <input
                  type="checkbox"
                  checked={permissions[permission]}
                  onChange={() => !isAdmin && !isClient && handleToggle(permission)}
                  disabled={isAdmin || isClient}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="font-medium">{PERMISSION_LABELS[permission]}</p>
                    {permissions[permission] && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
                        Ativo
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {PERMISSION_DESCRIPTIONS[permission]}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </CardContent>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t">
          <Button variant="outline" onClick={onClose} disabled={updatePermissionsMutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updatePermissionsMutation.isPending || isAdmin || isClient}
          >
            {updatePermissionsMutation.isPending ? 'Salvando...' : 'Salvar Permissões'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
